import { useMenuItem } from "@react-aria/menu";
import { TreeState } from "@react-stately/tree";
import { DOMAttributes, Node } from "@react-types/shared";
import React, { PropsWithChildren, ReactNode } from "react";
import styled, { css } from "styled-components";
import { PropsWithPropStyling, propStyling } from "../../shared/propStyling";
import { TextStyleProps, textStyles } from "../../shared/textStyles";
import { tombac } from "../../shared/tombac";

/** Props for the MenuItem component. */
export type MenuItemProps = PropsWithPropStyling<
  PropsWithChildren<
    DOMAttributes &
      TextStyleProps & {
        /**
         * The content of the menu item, which can be a `ReactNode` or a
         * function that returns a `ReactNode`. The function receives an object
         * with `descriptionProps` and `labelProps` that should be applied to
         * the description and label elements, respectively, if they exist.
         */
        children?:
          | ReactNode
          | ((renderProps: {
              descriptionProps: DOMAttributes;
              labelProps: DOMAttributes;
            }) => ReactNode);
        /**
         * Additional props to be applied to the container of the main content
         * when `prepend` is used. If `prepend` is not used, these props are
         * applied to the main component container, which is an equivalent to
         * putting the props directly on the component.
         */
        contentContainerProps?: PropsWithPropStyling<
          TextStyleProps & DOMAttributes
        >;
        /**
         * An optional element to be rendered before the main content of the
         * menu item, e.g. an icon.
         */
        prepend?: ReactNode;
        /**
         * Additional props to be applied to the container of the prepended
         * element. Has no effect if `prepend` is not provided.
         */
        prependContainerProps?: PropsWithPropStyling<
          TextStyleProps & DOMAttributes
        >;
        /**
         * An optional text value for the menu item, which can be used for
         * accessibility purposes, e.g. for selecting the item by typing its
         * name.
         */
        textValue?: string;
      }
  >
>;

export type MenuItemRendererProps<T extends object> = {
  item: Omit<Node<T>, "props" | "rendered"> & {
    props?: MenuItemProps;
    rendered: MenuItemProps["children"];
  };
  treeState: TreeState<T>;
};

export function MenuItemRenderer<T extends object>({
  item,
  treeState,
}: MenuItemRendererProps<T>) {
  const {
    contentContainerProps,
    prepend,
    prependContainerProps,
    ...itemProps
  } = item.props || {};

  const itemRef = React.useRef<HTMLLIElement>(null);

  const { descriptionProps, labelProps, isFocused, menuItemProps } =
    useMenuItem<T>({ key: item.key }, treeState, itemRef);

  const rendered =
    typeof item.rendered === "function"
      ? item.rendered({ descriptionProps, labelProps })
      : item.rendered;

  return (
    <Container
      {...itemProps}
      {...contentContainerProps}
      {...menuItemProps}
      isFocused={isFocused}
      hasPrepend={!!prepend}
      ref={itemRef}
    >
      {prepend ? (
        <>
          <Prepended {...prependContainerProps}>{prepend}</Prepended>
          <Content {...contentContainerProps}>{rendered}</Content>
        </>
      ) : (
        rendered
      )}
    </Container>
  );
}

const Container = styled.li<
  PropsWithPropStyling<{ isFocused: boolean; hasPrepend: boolean }>
>`
  all: initial;
  cursor: pointer;
  padding: ${tombac.space(0.5, 2)};
  ${tombac.text()}

  ${({ hasPrepend }) =>
    hasPrepend &&
    css`
      display: flex;
      align-items: center;
    `}

  ${({ isFocused }) =>
    isFocused &&
    css`
      background-color: ${tombac.color("neutral", 300)};
    `}

  ${propStyling}
  ${textStyles}
`;

const Content = styled.div`
  all: revert;
  ${propStyling}
  ${textStyles}
`;

const Prepended = styled.div`
  all: revert;
  margin-right: ${tombac.space(1)};
  min-width: ${tombac.space(3)};
  text-align: center;
  ${propStyling}
  ${textStyles}
`;
